import React, { createContext, useState, useContext, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../config/supabaseConfig.ts'

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

const UNAUTHORIZED_MSG = 'Acceso no autorizado';

// Mapea el usuario de Supabase a un objeto compatible con los componentes existentes.
// El rol 'admin' SOLO se asigna si la BD lo confirma (platform_admins vía RPC);
// user_metadata.role es editable por el usuario y solo se conserva como dato de despliegue.
// `staff` = { role: 'admin' | 'employee' | null, projectId } según la BD (script 47). El rol nunca sale de user_metadata: lo edita el usuario.
const mapUser = (user, staff) => {
  return {
    ...user,
    full_name: user.user_metadata?.full_name || user.email,
    metadata_role: user.user_metadata?.role || null,
    role: staff?.role || null,
    employeeProject: staff?.role === 'employee' ? staff.projectId ?? null : null,
  };
};

// Mismo usuario si no cambió nada de lo que la interfaz usa: así un evento de auth repetido (volver a la pestaña, renovar el token)
// no crea un currentUser nuevo ni vuelve a pintar toda la aplicación.
const sameUser = (a, b) => Boolean(a && b) && ['id', 'email', 'full_name', 'role', 'metadata_role', 'employeeProject'].every((k) => a[k] === b[k]);
const keepIfSame = (next) => (prev) => (sameUser(prev, next) ? prev : next);

// Consulta a la BD qué es el usuario del JWT actual: admin de plataforma, empleado o nadie. Sin el script 47 (my_staff_role no existe)
// se comporta como antes: solo los admins entran.
const fetchStaff = async () => {
  const { data, error } = await supabase.rpc('my_staff_role');
  if (!error && data && typeof data === 'object') {
    return { role: data.role === 'admin' || data.role === 'employee' ? data.role : null, projectId: data.project_id ?? null };
  }
  const legacy = await supabase.rpc('is_platform_admin');
  if (legacy.error) {
    console.error('[AuthContext] Error al verificar el rol:', legacy.error);
    return { role: null, projectId: null };
  }
  return { role: legacy.data === true ? 'admin' : null, projectId: null };
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Contador para descartar verificaciones obsoletas (eventos de auth encadenados)
  const checkSeq = useRef(0);
  // auth_user_id ya verificado como admin (evita re-consultar en cada refresh de token)
  const verifiedStaff = useRef(null);   // { id, staff } del usuario ya verificado

  // Login solo con Supabase Auth. El fallback a login-proxy (migración de hashes
  // bcrypt legacy) se retiró: permitía revivir contraseñas viejas y devolvía tokens.
  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      const isInvalidCredentials =
        error.message?.toLowerCase().includes('invalid login credentials') ||
        error.code === 'invalid_credentials' ||
        error.status === 400;
      throw isInvalidCredentials ? new Error('Credenciales inválidas') : error;
    }

    return ensureStaff(data.user);
  };

  // Verifica en BD que el usuario autenticado sea admin o empleado; si no, cierra sesión
  const ensureStaff = async (user) => {
    const staff = await fetchStaff();
    if (!staff.role) {
      await supabase.auth.signOut();
      throw new Error(UNAUTHORIZED_MSG);
    }
    return user;
  };

  // Logout con Supabase Auth
  const logout = async () => {
    await supabase.auth.signOut();
  };

  // Resuelve la sesión: solo expone currentUser si la BD confirma que es admin o empleado
  const resolveSession = async (session) => {
    const seq = ++checkSeq.current;
    if (!session) {
      verifiedStaff.current = null;
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    if (verifiedStaff.current?.id === session.user.id) {
      setCurrentUser(keepIfSame(mapUser(session.user, verifiedStaff.current.staff)));
      setLoading(false);
      return;
    }
    const staff = await fetchStaff();
    if (seq !== checkSeq.current) return; // llegó un evento más reciente
    if (!staff.role) {
      verifiedStaff.current = null;
      setCurrentUser(null);
      setLoading(false);
      await supabase.auth.signOut();
      return;
    }
    verifiedStaff.current = { id: session.user.id, staff };
    setCurrentUser(keepIfSame(mapUser(session.user, staff)));
    setLoading(false);
  };

  // Inicializar sesión y suscribirse a cambios de auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      resolveSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Diferido: no llamar a supabase (rpc) dentro del callback para evitar bloqueos de supabase-js
      setTimeout(() => resolveSession(session), 0);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context value
  // Memoizado: si el valor cambiara en cada render, todos los que usan useAuth se volverían a pintar.
  const value = useMemo(() => ({
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    role: currentUser?.role || null,
    isAdmin: currentUser?.role === 'admin',
    isEmployee: currentUser?.role === 'employee',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
